import React from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import keyMirror from 'keymirror';

import Ansi from 'ansi-to-react';
import ScrollableFeed from 'react-scrollable-feed';

import Box from '../box/box.jsx';
import Modal from '../../containers/modal.jsx';
import Dots from '../connection-modal/dots.jsx';

import styles from './upload-progress.css';

const PHASES = keyMirror({
    uploading: null,
    success: null,
    error: null,
    timeout: null,
    aborted: null
});

class UploadProgressComponent extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            isTerminalExpanded: false
        };
        this.toggleTerminal = this.toggleTerminal.bind(this);
    }

    toggleTerminal () {
        this.setState(state => ({
            isTerminalExpanded: !state.isTerminalExpanded
        }));
    }

    componentDidUpdate (prevProps) {
        if (this.props.isFinishing && !prevProps.isFinishing) {
            this.setState({isTerminalExpanded: false});
        }
    }

    render () {
        const {
            abortEnabled,
            autoCloseCount,
            connectionSmallIconURL,
            name,
            onAbort,
            onCancel,
            onHelp,
            onStopAutoClose,
            phase,
            isFinishing,
            scrollableRef,
            text
        } = this.props;

        return (
            <Modal
                className={classNames(styles.modalContent, {
                    [styles.modalContentExpanded]: this.state.isTerminalExpanded
                })}
                closeButtonVisible={false}
                contentLabel={name}
                headerClassName={styles.header}
                headerImage={connectionSmallIconURL}
                id="connectionModal"
                onHelp={onHelp}
                onRequestClose={onStopAutoClose}
                overlayClassName={styles.modalOverlay}
                shouldCloseOnOverlayClick
            >
                <Box
                    className={styles.body}
                >
                    {this.state.isTerminalExpanded && (
                        <Box className={styles.terminalWarper}>
                            <ScrollableFeed
                                className={styles.terminalText}
                                ref={scrollableRef}
                            >
                                <Ansi>
                                    {text}
                                </Ansi>
                            </ScrollableFeed>
                        </Box>
                    )}
                    <div
                        className={styles.terminalToggleButton}
                        onClick={this.toggleTerminal}
                    >
                        {this.state.isTerminalExpanded ? (
                            <FormattedMessage
                                defaultMessage="Hide Terminal"
                                description="Button to hide terminal output"
                                id="gui.uploadProgress.hideTerminal"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Show Terminal"
                                description="Button to show terminal output"
                                id="gui.uploadProgress.showTerminal"
                            />
                        )}
                    </div>
                    {/* <Dots
                        className={styles.bottomAreaItem}
                        counter={0}
                        total={6}
                        bouncing
                        transparent
                    /> */}
                    <Box className={styles.bottomArea}>
                        <Box className={classNames(
                            styles.bottomAreaItem,
                            styles.instructions,
                            {[styles.instructionsComplete]: phase === PHASES.success || isFinishing},
                            {[styles.instructionsError]: phase === PHASES.error || phase === PHASES.timeout}
                        )}>
                            {phase === PHASES.uploading ? (
                                isFinishing ? (
                                    <span>100% completed</span>
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Uploading"
                                        description="Prompt for upload in progress"
                                        id="gui.uploadProgress.uploading"
                                    />
                                )
                            ) : null}
                            {phase === PHASES.success ? (
                                <FormattedMessage
                                    defaultMessage="Code uploaded"
                                    description="Prompt for upload success"
                                    id="gui.uploadProgress.uploadSuccess"
                                />
                            ) : null}
                            {phase === PHASES.error ? (
                                <FormattedMessage
                                    defaultMessage="Upload error"
                                    description="Prompt for upload error"
                                    id="gui.uploadProgress.uploadError"
                                />
                            ) : null}
                            {phase === PHASES.timeout ? (
                                <FormattedMessage
                                    defaultMessage="Upload timeout"
                                    description="Prompt for upload timeout"
                                    id="gui.uploadProgress.uploadTimeout"
                                />
                            ) : null}
                            {phase === PHASES.aborted ? (
                                <FormattedMessage
                                    defaultMessage="Upload aborted"
                                    description="Prompt for upload aborted"
                                    id="gui.uploadProgress.uploadAborted"
                                />
                            ) : null}
                        </Box>
                        {
                            (phase === PHASES.uploading && abortEnabled) ?
                                <button
                                    className={classNames(styles.bottomAreaItem, styles.connectionButton)}
                                    onClick={onAbort}
                                >
                                    <FormattedMessage
                                        defaultMessage="Abort"
                                        description="Button in bottom to abort upload process"
                                        id="gui.uploadProgress.abort"
                                    />
                                </button> :
                                <button
                                    className={classNames(styles.bottomAreaItem, styles.connectionButton)}
                                    disabled={phase === PHASES.uploading}
                                    onClick={onCancel}
                                >
                                    <FormattedMessage
                                        defaultMessage="Close"
                                        description="Button in bottom to close after upload"
                                        id="gui.uploadProgress.close"
                                    />
                                    &nbsp;
                                    {((phase === PHASES.success ||
                                        phase === PHASES.aborted) &&
                                        autoCloseCount !== 0) ?
                                        `(${autoCloseCount / 1000}s)` : null}
                                </button>
                        }
                    </Box>
                </Box>
            </Modal>
        );
    }
}

UploadProgressComponent.propTypes = {
    connectionSmallIconURL: PropTypes.string,
    name: PropTypes.node,
    abortEnabled: PropTypes.bool.isRequired,
    autoCloseCount: PropTypes.number.isRequired,
    onAbort: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onHelp: PropTypes.func.isRequired,
    onStopAutoClose: PropTypes.func.isRequired,
    text: PropTypes.string.isRequired,
    phase: PropTypes.oneOf(Object.keys(PHASES)).isRequired,
    isFinishing: PropTypes.bool,
    scrollableRef: PropTypes.object // eslint-disable-line react/forbid-prop-types
};

UploadProgressComponent.defaultProps = {
};

export {
    UploadProgressComponent as default,
    PHASES
};
